import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getHubConfig, getCategoryHubConfig } from './modelHubConfig';

const RESULT_PAGE_SIZE = 24;

// ─── Action types ───────────────────────────────────────────────────────────

export const LISTINGS_REQUEST = 'app/ModelHubPage/LISTINGS_REQUEST';
export const LISTINGS_SUCCESS = 'app/ModelHubPage/LISTINGS_SUCCESS';
export const LISTINGS_ERROR = 'app/ModelHubPage/LISTINGS_ERROR';

// ─── Reducer ────────────────────────────────────────────────────────────────

const initialState = {
  pagination: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
};

const resultIds = data =>
  data.data
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case LISTINGS_REQUEST:
      return { ...state, searchInProgress: true, searchListingsError: null };
    case LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case LISTINGS_ERROR:
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };
    default:
      return state;
  }
};

export default reducer;

// ─── Action creators ────────────────────────────────────────────────────────

const listingsRequest = () => ({ type: LISTINGS_REQUEST });
const listingsSuccess = response => ({ type: LISTINGS_SUCCESS, payload: { data: response.data } });
const listingsError = e => ({ type: LISTINGS_ERROR, error: true, payload: e });

// ─── loadData (SSR entry point) ─────────────────────────────────────────────

/**
 * SSR data loader for model/category hub pages.
 * Called by the server before rendering — results are in Redux state at render time.
 *
 * Supports two route patterns:
 *   /cars/:modelSlug  →  params.modelSlug
 *   /parts, /memorabilia  →  params.categoryPath (set via extraProps in routeConfiguration)
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Resolve hub config from either model slug or category path
  const hubConfig = params.modelSlug
    ? getHubConfig(params.modelSlug)
    : params.categoryPath
    ? getCategoryHubConfig(params.categoryPath)
    : null;

  if (!hubConfig) {
    return Promise.resolve();
  }

  dispatch(listingsRequest());

  const { page = 1 } = parse(search);
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      ...hubConfig.searchParams,
      page,
      perPage: RESULT_PAGE_SIZE,
      pub_sold: false,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['author', 'images'],
      'fields.listing': ['title', 'geolocation', 'price', 'deleted', 'state', 'publicData', 'metadata'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 3,
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      dispatch(listingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(listingsError(error));
      throw e;
    });
};
